import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  adminVerify, adminStats, adminLeads, adminDocuments, adminLogout, adminUpdateLead,
} from "@/lib/apiClient";

const EMPTY_STATS = { leads: 0, documents: 0, by_status: { new: 0, contacted: 0, in_progress: 0, closed: 0 } };

/**
 * Data-fetching + session handling for the admin dashboard.
 * Authentication relies on the httpOnly session cookie set by the backend,
 * so nothing sensitive is ever stored in localStorage.
 */
export const useAdminData = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [leads, setLeads] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await adminLogout(); } catch { /* cookie may already be gone */ }
    navigate("/admin/login");
  }, [navigate]);

  const loadAll = useCallback(async (q = "", status = "") => {
    setLoading(true);
    try {
      const [s, l, d] = await Promise.all([adminStats(), adminLeads(q, status), adminDocuments()]);
      setStats({ ...EMPTY_STATS, ...s, by_status: { ...EMPTY_STATS.by_status, ...(s.by_status || {}) } });
      setLeads(l.leads || []);
      setDocs(d.documents || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    adminVerify()
      .then(() => { if (!cancelled) return loadAll(); })
      .catch(() => { if (!cancelled) navigate("/admin/login"); });
    return () => { cancelled = true; };
  }, [loadAll, navigate]);

  /** PATCH a lead (status / internal notes) and merge the result into local state. */
  const updateLead = useCallback(async (leadId, patch) => {
    try {
      const updated = await adminUpdateLead(leadId, patch);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
      // Keep status counters in sync without a full reload.
      if (patch.status) {
        const s = await adminStats();
        setStats((prev) => ({ ...prev, ...s, by_status: { ...prev.by_status, ...(s.by_status || {}) } }));
      }
      return updated;
    } catch (err) {
      if (err?.response?.status === 401) logout();
      throw err;
    }
  }, [logout]);

  const leadName = useCallback((id) => {
    const l = leads.find((x) => x.id === id);
    return l ? l.name : "\u2014";
  }, [leads]);

  return { stats, leads, docs, loading, loadAll, logout, leadName, updateLead };
};
